import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, Trophy, User, Cpu } from 'lucide-react';

const TicTacToe = () => {
    const [board, setBoard] = useState(Array(9).fill(null));
    const [isXNext, setIsXNext] = useState(true);
    const [winner, setWinner] = useState(null);
    const [winningLine, setWinningLine] = useState([]);

    const calculateWinner = (squares) => {
        const lines = [
            [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
            [0, 3, 6], [1, 4, 7], [2, 5, 8], // Cols
            [0, 4, 8], [2, 4, 6]              // Diagonals
        ];
        for (let i = 0; i < lines.length; i++) {
            const [a, b, c] = lines[i];
            if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
                return { winner: squares[a], line: lines[i] };
            }
        }
        if (squares.every(s => s !== null)) return { winner: 'Draw', line: [] };
        return null;
    };

    const handleClick = (i) => {
        if (board[i] || winner) return;
        const newBoard = [...board];
        newBoard[i] = isXNext ? 'X' : 'O';
        setBoard(newBoard);
        setIsXNext(!isXNext);
        
        const result = calculateWinner(newBoard);
        if (result) {
            setWinner(result.winner);
            setWinningLine(result.line);
        }
    };

    const resetGame = () => {
        setBoard(Array(9).fill(null));
        setIsXNext(true);
        setWinner(null);
        setWinningLine([]);
    };

    return (
        <div className="game-container glass-card">
            <div className="game-header">
                <h3>Tic <span className="gradient-text">Tac</span> Toe</h3>
                <p>Take a break and play!</p>
            </div>

            <div className="game-status">
                <div className={`status-badge ${isXNext ? 'active' : ''}`}>
                    <User size={16} /> <span>You (X)</span>
                </div>
                <div className={`status-badge ${!isXNext && !winner ? 'active' : ''}`}>
                    <Cpu size={16} /> <span>Next (O)</span>
                </div>
            </div>

            <div className="board">
                {board.map((cell, i) => (
                    <motion.button
                        key={i}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`cell ${winningLine.includes(i) ? 'winning' : ''}`}
                        onClick={() => handleClick(i)}
                    >
                        <AnimatePresence mode="wait">
                            {cell && (
                                <motion.span
                                    initial={{ scale: 0, rotate: -45 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    exit={{ scale: 0 }}
                                    className={cell === 'X' ? 'x-mark' : 'o-mark'}
                                >
                                    {cell}
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </motion.button>
                ))}
            </div>

            <AnimatePresence>
                {winner && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="game-result"
                    >
                        {winner === 'Draw' ? (
                            <p>It's a Draw! 🤝</p>
                        ) : (
                            <p className="winner-announcement">
                                <Trophy size={18} className="trophy-icon" />
                                {winner} Won!
                            </p>
                        )}
                        <button onClick={resetGame} className="btn btn-primary btn-sm btn-reset">
                            Play Again <RotateCcw size={14} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {!winner && (
                <button onClick={resetGame} className="icon-btn-text" style={{marginTop: '1rem'}}>
                    <RotateCcw size={14} /> Restart
                </button>
            )}
        </div>
    );
};

export default TicTacToe;
